import { useState } from 'react';
import { banksApi } from '../../api/services';
import type { BankAccountDto, BankDto, BankMovementDto, CheckInvoiceDto, ReconciliationResultDto, SaveBankAccountDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Check, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Table, Tabs, Toolbar,
  firstOfMonthIso, fmtDate, fmtMoney, money, round2, todayIso, useAction, useAsync,
} from '../../ui/components';
import { AccountPicker, BankAccountSelect, CurrencySelect, TemplateSelect } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';

type Tab = 'accounts' | 'movements' | 'operations' | 'checks' | 'reconciliation' | 'banks';

export function BanksPage() {
  const [tab, setTab] = useState<Tab>('accounts');
  return (
    <>
      <PageHeader title="Bancos" />
      <PageHelp page="banks" />
      <Tabs<Tab> tabs={[{ key: 'accounts', label: 'Cuentas bancarias' }, { key: 'movements', label: 'Movimientos' }, { key: 'operations', label: 'Depósitos / retiros / transferencias' }, { key: 'checks', label: 'Cheques' }, { key: 'reconciliation', label: 'Conciliación' }, { key: 'banks', label: 'Bancos' }]} active={tab} onChange={setTab} />
      {tab === 'banks' && <BanksTab />}
      {tab === 'accounts' && <AccountsTab />}
      {tab === 'movements' && <MovementsTab />}
      {tab === 'operations' && <OperationsTab />}
      {tab === 'checks' && <ChecksTab />}
      {tab === 'reconciliation' && <ReconciliationTab />}
    </>
  );
}

function BanksTab() {
  const list = useAsync(() => banksApi.banks(), []);
  const [editing, setEditing] = useState<BankDto | 'new' | null>(null);
  const [v, setV] = useState({ name: '', phone: '', address: '' });
  const { run, busy } = useAction();
  const open = (b: BankDto | 'new') => { setEditing(b); setV(b === 'new' ? { name: '', phone: '', address: '' } : { name: b.name, phone: b.phone ?? '', address: b.address ?? '' }); };
  return (
    <>
      <Toolbar><Button kind="primary" onClick={() => open('new')}>Nuevo banco</Button></Toolbar>
      <Loadable state={list}>
        {(rows) => <Table rows={rows} rowKey={(b) => b.code} onRowClick={open} columns={[
          { key: 'c', title: 'Código', render: (b) => b.code }, { key: 'n', title: 'Nombre', render: (b) => b.name },
          { key: 't', title: 'Teléfono', render: (b) => b.phone ?? '' }, { key: 'a', title: 'Cuentas', align: 'right', render: (b) => b.accountCount },
        ]} />}
      </Loadable>
      {editing && (
        <Modal title={editing === 'new' ? 'Nuevo banco' : `Banco ${editing.code}`} onClose={() => setEditing(null)} size="sm">
          <Form onSubmit={() => run(async () => { const dto = { name: v.name.trim(), phone: v.phone.trim() || null, address: v.address.trim() || null }; if (editing === 'new') await banksApi.createBank(dto); else await banksApi.updateBank(editing.code, dto); setEditing(null); list.reload(); }, { success: 'Banco guardado.' })}>
            <Field label="Nombre"><Input value={v.name} onChange={(x) => setV({ ...v, name: x })} required maxLength={100} /></Field>
            <Field label="Teléfono"><Input value={v.phone} onChange={(x) => setV({ ...v, phone: x })} maxLength={20} /></Field>
            <Field label="Dirección"><Input value={v.address} onChange={(x) => setV({ ...v, address: x })} maxLength={200} /></Field>
            <div className="acc-actions"><Button onClick={() => setEditing(null)}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
          </Form>
        </Modal>
      )}
    </>
  );
}

function AccountsTab() {
  const list = useAsync(() => banksApi.accounts(), []);
  const banks = useAsync(() => banksApi.banks(), []);
  const [editing, setEditing] = useState<BankAccountDto | 'new' | null>(null);
  const [v, setV] = useState<SaveBankAccountDto>({ bankCode: 0, bankAccountNumber: '', accountingAccount: '', accountType: 'Corriente', currencyId: 0, openingBalance: 0, status: 'Activo' });
  const { run, busy } = useAction();
  const open = (a: BankAccountDto | 'new') => {
    setEditing(a);
    setV(a === 'new' ? { bankCode: 0, bankAccountNumber: '', accountingAccount: '', accountType: 'Corriente', currencyId: 0, openingBalance: 0, status: 'Activo' }
      : { bankCode: a.bankCode, bankAccountNumber: a.bankAccountNumber, accountingAccount: a.accountingAccount, accountType: a.accountType, currencyId: a.currencyId, openingBalance: a.registeredBalance, status: a.status });
  };
  return (
    <>
      <Toolbar><Button kind="primary" onClick={() => open('new')}>Nueva cuenta bancaria</Button></Toolbar>
      <Loadable state={list}>
        {(rows) => <Table rows={rows} rowKey={(a) => a.code} onRowClick={open} columns={[
          { key: 'b', title: 'Banco', render: (a) => a.bankName ?? a.bankCode },
          { key: 'n', title: 'Número', render: (a) => a.bankAccountNumber },
          { key: 'c', title: 'Cuenta contable', render: (a) => `${a.accountingAccount}${a.accountingAccountName ? ` · ${a.accountingAccountName}` : ''}` },
          { key: 'm', title: 'Moneda', render: (a) => a.currencyName ?? a.currencyId },
          money<BankAccountDto>('s', 'Saldo contable (base)', (a) => a.accountingBalance),
          money<BankAccountDto>('o', 'Saldo moneda original', (a) => a.accountingBalanceOriginal),
          { key: 'e', title: 'Estado', render: (a) => <Badge tone={a.status.toUpperCase().startsWith('I') ? 'bad' : 'ok'}>{a.status}</Badge> },
        ]} />}
      </Loadable>
      {editing && (
        <Modal title={editing === 'new' ? 'Nueva cuenta bancaria' : `Cuenta ${editing.bankAccountNumber}`} onClose={() => setEditing(null)}>
          <Form onSubmit={() => run(async () => { if (editing === 'new') await banksApi.createAccount(v); else await banksApi.updateAccount(editing.code, v); setEditing(null); list.reload(); }, { success: 'Cuenta bancaria guardada.' })}>
            <div className="acc-grid-form">
              <Field label="Banco"><Select value={v.bankCode || ''} onChange={(x) => setV({ ...v, bankCode: x })} placeholder="Seleccione" options={(banks.data ?? []).map((b) => ({ value: b.code, label: b.name }))} /></Field>
              <Field label="Número de cuenta"><Input value={v.bankAccountNumber} onChange={(x) => setV({ ...v, bankAccountNumber: x })} required maxLength={80} /></Field>
              <Field label="Cuenta contable (detalle)" hint="No se puede cambiar si ya tiene movimientos"><AccountPicker value={v.accountingAccount} onChange={(x) => setV({ ...v, accountingAccount: x })} /></Field>
              <Field label="Tipo"><Input value={v.accountType} onChange={(x) => setV({ ...v, accountType: x })} required maxLength={10} /></Field>
              <Field label="Moneda"><CurrencySelect value={v.currencyId || null} onChange={(x) => setV({ ...v, currencyId: x })} /></Field>
              <Field label="Saldo registrado"><NumberInput value={v.openingBalance} onChange={(x) => setV({ ...v, openingBalance: x })} /></Field>
              <Field label="Estado"><Select value={v.status} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'Activo', label: 'Activo' }, { value: 'Inactivo', label: 'Inactivo' }]} /></Field>
            </div>
            <div className="acc-actions"><Button onClick={() => setEditing(null)}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
          </Form>
        </Modal>
      )}
    </>
  );
}

function MovementsTab() {
  const [account, setAccount] = useState('');
  const [from, setFrom] = useState(firstOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const state = useAsync(() => (account ? banksApi.movements(account, from, to) : Promise.resolve([] as BankMovementDto[])), [account, from, to]);
  return (
    <>
      <Toolbar>
        <BankAccountSelect value={account} onChange={setAccount} />
        <Field label="Desde"><Input type="date" value={from} onChange={setFrom} /></Field>
        <Field label="Hasta"><Input type="date" value={to} onChange={setTo} /></Field>
      </Toolbar>
      {!account ? <Alert>Seleccione una cuenta bancaria.</Alert> : (
        <Loadable state={state}>
          {(rows) => <Table rows={rows} rowKey={(m) => `${m.entryCode}-${m.lineNumber}`} columns={[
            { key: 'f', title: 'Fecha', render: (m) => fmtDate(m.date) },
            { key: 'a', title: 'Asiento', render: (m) => <a href={`#/entries/${encodeURIComponent(m.entryCode)}`}>{m.entryCode}</a> },
            { key: 'd', title: 'Descripción', render: (m) => m.description },
            { key: 'e', title: 'Estado', render: (m) => m.entryStatus },
            money<BankMovementDto>('db', 'Débito', (m) => m.debit),
            money<BankMovementDto>('cr', 'Crédito', (m) => m.credit),
            money<BankMovementDto>('s', 'Saldo', (m) => m.runningBalance),
            { key: 'c', title: 'Conciliación', render: (m) => m.reconciliationStatus ?? '' },
          ]} />}
        </Loadable>
      )}
    </>
  );
}

function OperationsTab() {
  const [kind, setKind] = useState<'deposit' | 'withdraw' | 'transfer'>('deposit');
  const [v, setV] = useState({ account: '', to: '', date: todayIso(), amount: 0, exchangeRate: null as number | null, description: '', reference: '', templateCode: null as number | null });
  const { run, busy } = useAction();
  const [last, setLast] = useState<string | null>(null);
  const submit = () => run(async () => {
    const base = { date: v.date, amount: round2(v.amount), exchangeRate: v.exchangeRate, description: v.description.trim(), reference: v.reference.trim() || null, templateCode: v.templateCode };
    const r = kind === 'transfer' ? await banksApi.transfer({ ...base, fromBankAccountCode: v.account, toBankAccountCode: v.to })
      : kind === 'deposit' ? await banksApi.deposit({ ...base, bankAccountCode: v.account }) : await banksApi.withdraw({ ...base, bankAccountCode: v.account });
    setLast(r.entryCode);
    setV({ ...v, amount: 0, description: '', reference: '' });
  }, { success: 'Movimiento registrado.' });
  return (
    <Card>
      <Tabs tabs={[{ key: 'deposit', label: 'Depósito' }, { key: 'withdraw', label: 'Retiro' }, { key: 'transfer', label: 'Transferencia' }]} active={kind} onChange={setKind} />
      <Form onSubmit={submit}>
        <div className="acc-grid-form">
          <Field label={kind === 'transfer' ? 'Cuenta origen' : 'Cuenta bancaria'}><BankAccountSelect value={v.account} onChange={(x) => setV({ ...v, account: x })} /></Field>
          {kind === 'transfer' && <Field label="Cuenta destino"><BankAccountSelect value={v.to} onChange={(x) => setV({ ...v, to: x })} /></Field>}
          <Field label="Fecha"><Input type="date" value={v.date} onChange={(x) => setV({ ...v, date: x })} required /></Field>
          <Field label="Monto"><NumberInput value={v.amount} onChange={(x) => setV({ ...v, amount: x })} min={0} /></Field>
          <Field label="Tipo de cambio" hint="Vacío = venta del día"><NumberInput step="0.00001" value={v.exchangeRate} onChange={(x) => setV({ ...v, exchangeRate: x || null })} /></Field>
          <Field label="Referencia"><Input value={v.reference} onChange={(x) => setV({ ...v, reference: x })} maxLength={60} /></Field>
          <Field label="Plantilla"><TemplateSelect moduleCode="BN" value={v.templateCode} onChange={(x) => setV({ ...v, templateCode: x })} /></Field>
        </div>
        <Field label="Descripción" wide><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} required maxLength={150} /></Field>
        {kind === 'transfer' && <Alert>Las transferencias exigen la misma moneda en ambas cuentas; para monedas distintas registre un retiro y un depósito.</Alert>}
        <div className="acc-actions">
          {last && <span>Último asiento: <a href={`#/entries/${encodeURIComponent(last)}`}>{last}</a></span>}
          <Button kind="primary" type="submit" busy={busy} disabled={!v.account || v.amount <= 0 || (kind === 'transfer' && (!v.to || v.to === v.account))}>Registrar</Button>
        </div>
      </Form>
    </Card>
  );
}

function ChecksTab() {
  const statuses = useAsync(() => banksApi.checkStatuses(), []);
  const [statusId, setStatusId] = useState<number | ''>('');
  const list = useAsync(() => banksApi.checks(statusId === '' ? undefined : statusId), [statusId]);
  const [creating, setCreating] = useState(false);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <Select value={statusId} onChange={setStatusId} placeholder="Todos los estados" options={(statuses.data ?? []).map((s) => ({ value: s.id, label: s.description }))} />
        <Button kind="primary" onClick={() => setCreating(true)}>Emitir cheque</Button>
      </Toolbar>
      <Loadable state={list}>
        {(rows) => <Table rows={rows} rowKey={(c) => c.id} columns={[
          { key: 'n', title: 'Cheque', render: (c) => c.number },
          { key: 'b', title: 'Banco', render: (c) => c.bankName ?? c.bankCode },
          { key: 'f', title: 'Fecha', render: (c) => fmtDate(c.date) },
          { key: 'm', title: 'Monto', align: 'right', render: (c) => fmtMoney(c.amount) },
          { key: 'e', title: 'Estado', render: (c) => c.statusName ?? c.statusId },
          { key: 'a', title: 'Asiento', render: (c) => <a href={`#/entries/${encodeURIComponent(c.entryCode)}`}>{c.entryCode}</a> },
          {
            key: 'x', title: '', render: (c) => (
              <span className="acc-row">
                <Button kind="link" busy={busy} onClick={() => run(() => banksApi.closeCheck(c.id), { confirm: 'Marcar el cheque como cobrado/cerrado?', success: 'Cheque cerrado.' }).then(list.reload)}>Cobrado</Button>
                <Button kind="link" busy={busy} onClick={() => run((reason) => banksApi.cancelCheck(c.id, reason), { confirm: 'Anular el cheque? Se anula su asiento con reverso.', danger: true, requireReason: true, success: 'Cheque anulado.' }).then(list.reload)}>Anular</Button>
              </span>
            ),
          },
        ]} />}
      </Loadable>
      {creating && <CheckForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); list.reload(); }} />}
    </>
  );
}

function CheckForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState({ bankAccountCode: '', number: '', date: todayIso(), amount: 0, exchangeRate: null as number | null, description: '', templateCode: null as number | null });
  const [invoices, setInvoices] = useState<CheckInvoiceDto[]>([]);
  const { run, busy } = useAction();
  const invTotal = round2(invoices.reduce((s, i) => s + (i.amount || 0), 0));
  const setInv = (k: number, p: Partial<CheckInvoiceDto>) => setInvoices(invoices.map((x, i) => (i === k ? { ...x, ...p } : x)));
  return (
    <Modal title="Emitir cheque" onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => { await banksApi.createCheck({ ...v, number: v.number.trim(), description: v.description.trim(), templateModule: 'BN', templateCode: v.templateCode!, invoices }); onSaved(); }, { success: 'Cheque emitido.' })}>
        <div className="acc-grid-form">
          <Field label="Cuenta bancaria"><BankAccountSelect value={v.bankAccountCode} onChange={(x) => setV({ ...v, bankAccountCode: x })} /></Field>
          <Field label="Número de cheque"><Input value={v.number} onChange={(x) => setV({ ...v, number: x })} required maxLength={25} /></Field>
          <Field label="Fecha"><Input type="date" value={v.date} onChange={(x) => setV({ ...v, date: x })} /></Field>
          <Field label="Monto"><NumberInput value={v.amount} onChange={(x) => setV({ ...v, amount: x })} min={0} /></Field>
          <Field label="Tipo de cambio"><NumberInput step="0.00001" value={v.exchangeRate} onChange={(x) => setV({ ...v, exchangeRate: x || null })} /></Field>
          <Field label="Plantilla (obligatoria)"><TemplateSelect moduleCode="BN" value={v.templateCode} onChange={(x) => setV({ ...v, templateCode: x })} allowDefault={false} /></Field>
        </div>
        <Field label="Descripción" wide><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} required maxLength={150} /></Field>
        <Card title="Facturas que paga el cheque (opcional)">
          <Table rows={invoices.map((x, i) => ({ ...x, i }))} rowKey={(x) => x.i} columns={[
            { key: 's', title: 'Proveedor', render: (x) => <NumberInput step="1" value={x.supplierCode || null} onChange={(n) => setInv(x.i, { supplierCode: n })} /> },
            { key: 'f', title: 'Factura', render: (x) => <Input value={x.supplierInvoice} onChange={(n) => setInv(x.i, { supplierInvoice: n })} /> },
            { key: 'd', title: 'Fecha', render: (x) => <Input type="date" value={x.invoiceDate} onChange={(n) => setInv(x.i, { invoiceDate: n })} /> },
            { key: 'c', title: 'Cuenta', render: (x) => <AccountPicker value={x.accountNumber} onChange={(n) => setInv(x.i, { accountNumber: n })} /> },
            { key: 'm', title: 'Monto', render: (x) => <NumberInput value={x.amount} onChange={(n) => setInv(x.i, { amount: n })} /> },
            { key: 'x', title: '', render: (x) => <Button kind="link" onClick={() => setInvoices(invoices.filter((_, i) => i !== x.i))}>Quitar</Button> },
          ]} />
          <Button onClick={() => setInvoices([...invoices, { supplierCode: 0, supplierInvoice: '', invoiceDate: todayIso(), accountNumber: '', amount: 0, detail: null }])}>Agregar factura</Button>
          {invoices.length > 0 && invTotal !== round2(v.amount) && <Alert kind="warn">La suma de facturas ({fmtMoney(invTotal)}) no coincide con el monto del cheque.</Alert>}
        </Card>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!v.bankAccountCode || !v.templateCode || v.amount <= 0}>Emitir</Button></div>
      </Form>
    </Modal>
  );
}

/** Conciliación: se pegan las líneas del extracto (fecha;monto;referencia;descripción) y la API las empareja con los movimientos. */
function ReconciliationTab() {
  const [v, setV] = useState({ account: '', from: firstOfMonthIso(), to: todayIso(), tolerance: 3, text: '', save: false });
  const [result, setResult] = useState<ReconciliationResultDto | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const { run, busy } = useAction();
  const parse = () => {
    const errs: string[] = [];
    const lines = v.text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).flatMap((l, i) => {
      const p = l.split(/[;\t]/);
      const date = (p[0] ?? '').trim();
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : /^(\d{2})\/(\d{2})\/(\d{4})$/.test(date) ? date.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$2-$1') : '';
      const amount = Number((p[1] ?? '').replace(/\s/g, '').replace(/,(?=\d{1,2}$)/, '.').replace(/,/g, ''));
      if (!iso || Number.isNaN(amount)) { errs.push(`Línea ${i + 1}: formato inválido`); return []; }
      return [{ date: iso, amount, reference: p[2]?.trim() || null, description: p[3]?.trim() || null }];
    });
    setParseErrors(errs);
    return errs.length ? null : lines;
  };
  return (
    <>
      <Card>
        <div className="acc-grid-form">
          <Field label="Cuenta bancaria"><BankAccountSelect value={v.account} onChange={(x) => setV({ ...v, account: x })} /></Field>
          <Field label="Desde"><Input type="date" value={v.from} onChange={(x) => setV({ ...v, from: x })} /></Field>
          <Field label="Hasta"><Input type="date" value={v.to} onChange={(x) => setV({ ...v, to: x })} /></Field>
          <Field label="Tolerancia (días)"><NumberInput step="1" value={v.tolerance} onChange={(x) => setV({ ...v, tolerance: x })} min={0} max={31} /></Field>
        </div>
        <Field label="Líneas del extracto" wide hint="Una por línea: fecha;monto;referencia;descripción. Monto positivo = depósito, negativo = retiro. Fecha AAAA-MM-DD o DD/MM/AAAA.">
          <textarea className="acc-input acc-textarea" rows={8} value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} />
        </Field>
        <Check checked={v.save} onChange={(x) => setV({ ...v, save: x })} label="Guardar resultado (requiere habilitar esta función en la base de datos)" />
        {parseErrors.map((e) => <Alert key={e} kind="error">{e}</Alert>)}
        <div className="acc-actions">
          <Button kind="primary" busy={busy} disabled={!v.account} onClick={() => { const lines = parse(); if (lines) run(async () => setResult(await banksApi.reconcile({ bankAccountCode: v.account, dateFrom: v.from, dateTo: v.to, toleranceDays: v.tolerance, statementLines: lines, save: v.save })), { success: 'Conciliación calculada.' }); }}>Conciliar</Button>
        </div>
      </Card>
      {result && (
        <Card title={`Resultado${result.saved ? ' (guardado)' : ''}`}>
          <p>Conciliados: <strong>{result.matched}</strong> · Con diferencia: <strong>{result.withDifference}</strong> · Pendientes en sistema: <strong>{result.pendingSystem}</strong> · Pendientes en extracto: <strong>{result.pendingStatement}</strong> · Total sistema {fmtMoney(result.systemTotal)} · Total extracto {fmtMoney(result.statementTotal)}</p>
          <Table rows={result.lines.map((l, i) => ({ ...l, i }))} rowKey={(l) => l.i} columns={[
            { key: 's', title: 'Estado', render: (l) => <Badge tone={l.status === 'Conciliado' ? 'ok' : l.status === 'Diferencia' ? 'warn' : 'bad'}>{l.status}</Badge> },
            { key: 'ef', title: 'Extracto fecha', render: (l) => fmtDate(l.statementDate) },
            { key: 'em', title: 'Extracto monto', align: 'right', render: (l) => fmtMoney(l.statementAmount) },
            { key: 'er', title: 'Referencia', render: (l) => l.statementReference ?? '' },
            { key: 'a', title: 'Asiento', render: (l) => l.entryCode ?? '' },
            { key: 'sf', title: 'Sistema fecha', render: (l) => fmtDate(l.entryDate) },
            { key: 'sm', title: 'Sistema monto', align: 'right', render: (l) => fmtMoney(l.systemAmount) },
            { key: 'd', title: 'Diferencia', align: 'right', render: (l) => fmtMoney(l.difference) },
          ]} />
        </Card>
      )}
    </>
  );
}
